import type React from 'react';

import { Image, SocialIcon } from '@change/ds-core-ui';
import type { Icon } from '@change/ds-icons/types';
import { cn } from '@change/ds-utils';

import { useCyclingIconSlots } from './hooks/useCyclingIconSlots';

const SLOT_COUNT = 3;

// leftmost badge in front
const SLOT_LAYER = ['z-30', 'z-20', 'z-10'] as const;

type SocialIconVariant = NonNullable<React.ComponentProps<typeof SocialIcon>['variant']>;

const SlotContent: React.FC<{ item: SourceIconStackItem }> = ({ item }) => {
	if (item.kind === 'image') {
		return <Image src={item.src} alt="" aspectRatio="1:1" className="h-full w-full object-contain" />;
	}
	if (item.kind === 'social') {
		// 32px, clipped to the inside of the ring by the badge's overflow
		return <SocialIcon variant={item.variant} size="medium" icon={item.icon} />;
	}
	const SlotIcon = item.icon;
	// no colour of its own: inherits the disc's, so an item className can recolour both together
	return <SlotIcon size="x-small" />;
};

/**
 * Three overlapping badges that take turns swapping to the next item in `items`. Sized to match the
 * hero helper's single badge. Decorative and hidden from assistive tech: the consumer names the
 * sources in text (see FormHelper's visually-hidden list). Static under reduced motion.
 */
export const SourceIconStack: React.FC<SourceIconStackProps> = ({ items, className }) => {
	const {
		data: { slots },
	} = useCyclingIconSlots(items.length, SLOT_COUNT);

	return (
		<div aria-hidden="true" data-qa="source-icon-stack" className={cn('flex shrink-0 items-center', className)}>
			{slots.map((itemIndex, slot) => {
				const item = items[itemIndex];
				return (
					<div
						key={slot}
						data-qa="source-icon-stack-slot"
						data-item-index={itemIndex}
						className={cn(
							'border-stroke-inverse-strong elevation-low relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border-2',
							item.kind === 'social' ? 'bg-fill-weaker' : cn('bg-fill-strong text-icon-inverse-strong', item.className),
							SLOT_LAYER[slot],
							slot > 0 && '-ml-2',
						)}
					>
						<div
							key={itemIndex}
							className="animate-source-icon-in flex h-full w-full items-center justify-center motion-reduce:animate-none"
						>
							<SlotContent item={item} />
						</div>
					</div>
				);
			})}
		</div>
	);
};

/**
 * An icon or logo image on a dark disc, or a brand-coloured `SocialIcon`. `className` restyles the disc;
 * an icon takes the disc's text colour, so a fixed brand background needs a `-fixed` icon token too.
 */
export type SourceIconStackItem =
	| { kind: 'icon'; icon: React.ComponentType<Icon>; className?: string }
	| { kind: 'image'; src: string; className?: string }
	| { kind: 'social'; variant: SocialIconVariant; icon: React.ReactElement };

export type SourceIconStackProps = Readonly<{
	/** Items to cycle through. Pass a module-level array so the list is stable between renders. */
	items: readonly SourceIconStackItem[];
	className?: string;
}>;
